import React from 'react';
import { Target, Brain, BarChart3, Lightbulb, FolderOpen } from 'lucide-react';

const HowItWorks: React.FC = () => {
  const storySteps = [
    {
      step: "1",
      title: "Capture Every Call",
      description: "100% call transcription ensures no conversation is missed.",
      icon: Target,
      story: "Every word captured and preserved."
    },
    {
      step: "2", 
      title: "AI Analysis",
      description: "Advanced sentiment and emotion analysis reveals conversation insights.",
      icon: Brain,
      story: "AI studies every interaction for patterns."
    },
    {
      step: "3",
      title: "Performance Scoring",
      description: "Instant scoring across empathy, problem-solving, and communication skills.",
      icon: BarChart3, 
      story: "Clear performance metrics for every agent."
    },
    {
      step: "4",
      title: "Smart Coaching",
      description: "Personalized feedback and real-time coaching suggestions for improvement.",
      icon: Lightbulb,
      story: "Actionable insights that drive performance."
    },
    {
      step: "5",
      title: "Organize & Track",
      description: "Automatic categorization and follow-up tracking for complete visibility.",
      icon: FolderOpen,
      story: "Never miss a follow-up again."
    }
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From call capture to performance coaching, our AI transforms your call center operations in 5 simple steps.
          </p>
        </div>

        {/* Story Flow */}
        <div className="relative">
          {/* Connecting Line */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-[#0E666F]/20 via-[#0E666F]/40 to-[#0E666F]/20"></div>
          
          <div className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-5 lg:gap-8">
            {storySteps.map((step, index) => (
              <div key={index} className="relative">
                {/* Step Number */}
                <div className="flex justify-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-[#0E666F] to-[#0A4A52] text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                    {step.step}
                  </div>
                </div>

                {/* Story Card */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#0E666F]/20 group text-center">
                  <div className="flex justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <step.icon className="w-8 h-8 text-[#0E666F]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#0E666F] transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">
                    {step.description}
                  </p>
                  <div className="text-xs text-[#0E666F] font-medium italic">
                    {step.story}
                  </div>
                </div>

                {/* Arrow for mobile */}
                {index < storySteps.length - 1 && (
                  <div className="lg:hidden flex justify-center mt-6">
                    <svg className="w-6 h-6 text-[#0E666F]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info Card */}
        {/* <div className="mt-16 bg-gradient-to-r from-[#0E666F] to-[#0A4A52] rounded-2xl p-8 text-white">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Transform Your Call Center?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              Start analyzing 100% of your calls with AI-powered insights today.
            </p>
            <a 
              href={APP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white text-[#0E666F] font-semibold px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-300 shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <span>Get Started Now</span>
            </a>
          </div>
        </div> */}
      </div>
    </section>
  );
};

export default HowItWorks;
